import { gql } from '@apollo/client'

// ===========================
// Type Definitions
// ===========================

export enum MemberRoleCode {
  BORROWER = 'borrower',
  CO_BORROWER = 'co_borrower',
  NON_BORROWER_OWNER = 'non_borrower_owner',
  AUTHORIZED_CONTACT = 'authorized_contact',
  AUTHORIZED_SIGNER = 'authorized_signer',
  AGENT = 'agent',
  AGENT_SECONDARY = 'agent_secondary',
  LENDER = 'lender',
  LENDER_SECONDARY = 'lender_secondary',
  MORTGAGE_BROKER = 'mortgage_broker',
  MORTGAGE_BROKER_SECONDARY = 'mortgage_broker_secondary',
  ACCOUNT_EXECUTIVE = 'account_executive',
  LISTING_MANAGER = 'listing_manager',
}

export type MemberRole = {
  id: number
  roleCode: MemberRoleCode | string
  roleName: string
}

export type PersonSummary = {
  id: number | null
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  mobilePhone: string | null
}

export type AccountSummary = {
  id: number
  accountId: number | null
  active: number | null
  created: string | null
}

export type TransactionMember = {
  id: number
  transactionId: number
  dealId: number | null
  personId: number | null
  accountId: number | null
  memberType: string | null
  role: MemberRole
  person: PersonSummary | null
  account: AccountSummary | null
  isPrimary: boolean
  memberSource: string | null
  createdByPersonId: number | null
  modifiedByPersonId: number | null
  modifiedReason: string | null
  activeStartDate: string
  activeEndDate: string | null
  deletedDate: string | null
  isDeleted: boolean
  updated: string | null
  created: string
}

export type LoanRoleInfo = {
  id: number
  /** Human-readable role label from the loan role taxonomy, e.g. "Loan Officer". */
  name: string
  /** Stable system name, e.g. "loan_officer", "underwriter", "sales_assistant". */
  systemName: string
  fullName: string | null
}

export type LoanRoleEmployee = {
  firstName: string | null
  lastName: string | null
  email: string | null
  title: string | null
  workPhoneNumber: string | null
  primaryPhone: string | null
  mobilePhone: string | null
  nmlsId: number | null
  calendarLink: string | null
  active: number | null
}

/**
 * A loan-role holder: an internal Knock employee associated with the
 * transaction's primary loan (loan officer, underwriter, lending/sales
 * assistant, etc.). Distinct from transaction members.
 */
export type TransactionLoanRole = {
  loanId: string
  accountId: number
  role: LoanRoleInfo
  employee: LoanRoleEmployee
  activeDate: string | null
  inactiveDate: string | null
}

export type SearchMemberResult = {
  personId: number
  accountId: number | null
  accountActive: boolean | null
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  mobilePhone: string | null
  active: boolean | null
}

// Types for person lookup (detail view)
export type PersonRoles = {
  isHomebuyer: boolean | null
  isAgent: boolean | null
  isLender: boolean | null
  isMortgageBroker: boolean | null
}

export type PersonMetadata = {
  nmlsId: string | null
  preferredPartner: boolean | null
  hasHomebuyerTransactions: boolean | null
  hasAgentLeads: boolean | null
  hasLenderLeads: boolean | null
  hasMortgageBrokerLeads: boolean | null
}

export type PersonDetail = {
  personId: number
  accountId: number | null
  email: string | null
  firstName: string | null
  lastName: string | null
  phone: string | null
  mobilePhone: string | null
  emailVerify: boolean | null
  notificationPreference: string | null
  isDeleted: boolean | null
  active: boolean | null
  employeeId: number | null
  roles: PersonRoles | null
  metadata: PersonMetadata | null
}

// ===========================
// Query Response Types
// ===========================

export type GetTransactionMembersResponse = {
  transactionMembers: TransactionMember[]
}

export type GetTransactionMembersHistoryResponse = {
  transactionMembersHistory: TransactionMember[]
}

export type GetTransactionLoanRolesResponse = {
  transactionLoanRoles: TransactionLoanRole[]
}

export type SearchMembersResponse = {
  searchMembers: {
    members: SearchMemberResult[]
    totalCount: number
  }
}

export type AddTransactionMemberResponse = {
  addTransactionMember: {
    success: boolean
    member: TransactionMember
    message?: string
  }
}

export type UpdateTransactionMemberResponse = {
  updateTransactionMember: {
    success: boolean
    member: TransactionMember
    message?: string
  }
}

export type RemoveTransactionMemberResponse = {
  removeTransactionMember: {
    success: boolean
    message?: string
  }
}

export type LookupPersonResponse = {
  lookupPerson: PersonDetail | null
}

// ===========================
// Query Input Types
// ===========================

export type GetTransactionMembersInput = {
  transactionId: number
  roleCode?: string[]
  state?: 'active' | 'inactive' | 'all'
  includeAccountData?: boolean
}

export type SearchMembersInput = {
  query?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  limit?: number
  offset?: number
  auth0Roles?: string[]
  auth0RolesMatch?: 'any' | 'all'
  doNotIncludeInactive?: boolean
  includeDeleted?: boolean
  personIds?: number[]
  accountIds?: number[]
}

export type AddTransactionMemberInput = {
  transactionId: number
  personId?: number
  accountId?: number
  roleCode: MemberRoleCode | string
  dealId?: number
  activeStartDate?: string
  activeEndDate?: string
}

export type UpdateTransactionMemberInput = {
  transactionId: number
  memberId: number
  dealId?: number
  activeEndDate?: string
}

export type RemoveTransactionMemberInput = {
  transactionId: number
  memberId: number
}

export type LookupPersonInput = {
  personId?: number
  accountId?: number
  includeLeadsCheck?: boolean
  includeInactive?: boolean
}

export type UpdatePersonInput = {
  personId: number
  firstName?: string
  lastName?: string
  phoneNumber?: string
  mobileNumber?: string
}

export type UpdatePersonResponse = {
  updatePerson: {
    is_duplicate_mobile_phone: boolean
  }
}

// GraphQL Query Variables Types
export type SearchMembersVariables = {
  input: SearchMembersInput
}

// ===========================
// GraphQL Queries (PLACEHOLDERS)
// ===========================

/**
 * Get active transaction members for a transaction
 * TODO: Implement backend resolver
 */
export const GET_TRANSACTION_MEMBERS = gql`
  query GetTransactionMembers($input: TransactionMembersInput!) {
    transactionMembers(input: $input) {
      id
      transactionId: transaction_id
      dealId: deal_id
      personId: person_id
      accountId: account_id
      role {
        id
        roleCode: role_code
        roleName: role_name
      }
      person {
        id
        firstName: first_name
        lastName: last_name
        email
        phone
        mobilePhone: mobile_phone
      }
      account {
        id
        accountId: account_id
        active
        created
      }
      isPrimary: is_primary
      memberSource: member_source
      createdByPersonId: created_by_person_id
      modifiedByPersonId: modified_by_person_id
      modifiedReason: modified_reason
      activeStartDate: active_start_date
      activeEndDate: active_end_date
      deletedDate: deleted_date
      isDeleted: is_deleted
      updated
      created
    }
  }
`

/**
 * Get historical transaction members (with end dates)
 * TODO: Implement backend resolver
 */
export const GET_TRANSACTION_MEMBERS_HISTORY = gql`
  query GetTransactionMembersHistory($input: TransactionMembersInput!) {
    transactionMembersHistory(input: $input) {
      id
      transactionId: transaction_id
      dealId: deal_id
      personId: person_id
      accountId: account_id
      role {
        id
        roleCode: role_code
        roleName: role_name
      }
      person {
        id
        firstName: first_name
        lastName: last_name
        email
        phone
        mobilePhone: mobile_phone
      }
      account {
        id
        accountId: account_id
        active
        created
      }
      isPrimary: is_primary
      memberSource: member_source
      createdByPersonId: created_by_person_id
      modifiedByPersonId: modified_by_person_id
      modifiedReason: modified_reason
      activeStartDate: active_start_date
      activeEndDate: active_end_date
      deletedDate: deleted_date
      isDeleted: is_deleted
      updated
      created
    }
  }
`

/**
 * Get loan roles (internal Knock employees on the transaction's primary loan)
 */
export const GET_TRANSACTION_LOAN_ROLES = gql`
  query GetTransactionLoanRoles($input: TransactionLoanRolesInput!) {
    transactionLoanRoles(input: $input) {
      loanId: loan_id
      accountId: account_id
      role {
        id
        name
        systemName: system_name
        fullName: full_name
      }
      employee {
        firstName: first_name
        lastName: last_name
        email
        title
        workPhoneNumber: work_phone_number
        primaryPhone: primary_phone
        mobilePhone: mobile_phone
        nmlsId: nmls_id
        calendarLink: calendar_link
        active
      }
      activeDate: active_date
      inactiveDate: inactive_date
    }
  }
`

/**
 * Search for members/people to add to transaction
 * TODO: Implement backend resolver
 */
export const SEARCH_MEMBERS = gql`
  query SearchMembers($input: SearchMembersInput!) {
    searchMembers(input: $input) {
      members {
        personId: person_id
        accountId: account_id
        accountActive: account_active
        firstName: first_name
        lastName: last_name
        email
        phone
        mobilePhone: mobile_phone
        active
      }
      totalCount
    }
  }
`

// ===========================
// GraphQL Mutations (PLACEHOLDERS)
// ===========================

/**
 * Add a member to a transaction
 * TODO: Implement backend resolver
 */
export const ADD_TRANSACTION_MEMBER = gql`
  mutation AddTransactionMember($input: AddTransactionMemberInput!) {
    addTransactionMember(input: $input) {
      success
      message
      member {
        id
        transactionId: transaction_id
        dealId: deal_id
        personId: person_id
        accountId: account_id
        role {
          id
          roleCode: role_code
          roleName: role_name
        }
        person {
          id
          firstName: first_name
          lastName: last_name
          email
          phone
          mobilePhone: mobile_phone
        }
        isPrimary: is_primary
        memberSource: member_source
        createdByPersonId: created_by_person_id
        modifiedByPersonId: modified_by_person_id
        modifiedReason: modified_reason
        activeStartDate: active_start_date
        activeEndDate: active_end_date
        deletedDate: deleted_date
        isDeleted: is_deleted
        updated
        created
      }
    }
  }
`

/**
 * Update a transaction member (change role or primary status)
 * TODO: Implement backend resolver
 */
export const UPDATE_TRANSACTION_MEMBER = gql`
  mutation UpdateTransactionMember($input: UpdateTransactionMemberInput!) {
    updateTransactionMember(input: $input) {
      success
      message
      member {
        id
        transactionId: transaction_id
        dealId: deal_id
        personId: person_id
        accountId: account_id
        role {
          id
          roleCode: role_code
          roleName: role_name
        }
        person {
          id
          firstName: first_name
          lastName: last_name
          email
          phone
          mobilePhone: mobile_phone
        }
        isPrimary: is_primary
        memberSource: member_source
        createdByPersonId: created_by_person_id
        modifiedByPersonId: modified_by_person_id
        modifiedReason: modified_reason
        activeStartDate: active_start_date
        activeEndDate: active_end_date
        deletedDate: deleted_date
        isDeleted: is_deleted
        updated
        created
      }
    }
  }
`

/**
 * Remove a member from a transaction (sets end_date)
 * TODO: Implement backend resolver
 */
export const REMOVE_TRANSACTION_MEMBER = gql`
  mutation RemoveTransactionMember($input: RemoveTransactionMemberInput!) {
    removeTransactionMember(input: $input) {
      success
      message
    }
  }
`

/**
 * Lookup a person by person_id or account_id
 * Returns detailed person info including roles and metadata
 */
export const LOOKUP_PERSON = gql`
  query LookupPerson($input: LookupPersonInput!) {
    lookupPerson(input: $input) {
      personId: person_id
      accountId: account_id
      email
      firstName: first_name
      lastName: last_name
      phone
      mobilePhone: mobile_phone
      emailVerify: email_verify
      notificationPreference: notification_preference
      isDeleted: is_deleted
      active
      employeeId: employee_id
      roles {
        isHomebuyer: is_homebuyer
        isAgent: is_agent
        isLender: is_lender
        isMortgageBroker: is_mortgage_broker
      }
      metadata {
        nmlsId: nmls_id
        preferredPartner: preferred_partner
        hasHomebuyerTransactions: has_homebuyer_transactions
        hasAgentLeads: has_agent_leads
        hasLenderLeads: has_lender_leads
        hasMortgageBrokerLeads: has_mortgage_broker_leads
      }
    }
  }
`

/**
 * Update a person's first name, last name, phone, and mobile number.
 * Matches backend: UpdatePersonInput (person_id!, first_name, last_name, phone_number, mobile_number), UpdatePersonPayload (is_duplicate_mobile_phone).
 */
export const UPDATE_PERSON = gql`
  mutation UpdatePerson($input: UpdatePersonInput!) {
    updatePerson(input: $input) {
      is_duplicate_mobile_phone
    }
  }
`
